import React, { useMemo, useState } from 'react';
import { getCardReferences, stripKeyPointsFromAnswer } from './clinicalCardUtils.js';
import { cardStyleLabel } from './clinicalOptions.js';
import CardImage from '../CardImage.jsx';

const formatAnswer = (answer) => {
    if (typeof answer !== 'string') return JSON.stringify(answer);
    return stripKeyPointsFromAnswer(answer);
};

const renderAnswer = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, i) => {
        if (line === '') return <br key={i} />;
        if (line.startsWith('## ')) return <p key={i} className="font-semibold text-white mt-2">{line.slice(3)}</p>;
        const parts = line.split(/(\*\*[^*]+\*\*)/g);
        const content = parts.length > 1
            ? parts.map((part, j) => /^\*\*[^*]+\*\*$/.test(part) ? <strong key={j} className="font-semibold text-slate-200">{part.slice(2, -2)}</strong> : part)
            : line;
        return <p key={i}>{content}</p>;
    });
};

const ClinicalGeneratedCard = ({
    card,
    selected,
    onSelectedChange,
    defaultExpanded = false,
}) => {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const meta = card.clinical || {};
    const question =
        typeof card.question === 'string' ? card.question : JSON.stringify(card.question);
    const answer = useMemo(() => formatAnswer(card.answer), [card.answer]);
    const citations = useMemo(() => getCardReferences(card), [card]);

    return (
        <article className="rounded-2xl border border-white/10 bg-slate-900/80 p-4 sm:p-5 shadow-lg shadow-black/20">
            <div className="flex items-start justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2 min-w-0">
                    <span className="text-sm font-semibold text-teal-400">
                        {cardStyleLabel(meta.cardStyle || 'Clinical Vignette', meta.examType)}
                    </span>
                    {meta.difficulty && (
                        <span className="inline-flex items-center rounded-full border border-slate-600 px-2.5 py-0.5 text-[11px] font-medium text-slate-400">
                            {meta.difficulty}
                        </span>
                    )}
                </div>
                <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-600 bg-slate-950/60 text-teal-500 focus:ring-teal-500/40"
                    checked={!!selected}
                    onChange={(e) => onSelectedChange(e.target.checked)}
                    aria-label="Select card"
                />
            </div>

            <p className="mt-4 text-[15px] leading-relaxed text-white">{question}</p>

            {card.imageUrl && (
                <CardImage
                    imageUrl={card.imageUrl}
                    alt="Attached clinical image"
                    className="mt-3 max-h-64 w-full rounded-xl border border-white/10 object-contain bg-slate-950/40"
                />
            )}

            {expanded ? (
                <div className="mt-4">
                    <div className="text-sm leading-relaxed text-slate-300 space-y-1">
                        {renderAnswer(answer)}
                    </div>
                    {citations.length > 0 && (
                        <div className="mt-4 rounded-xl border border-white/10 bg-slate-950/40 p-4">
                            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                Citations
                            </h4>
                            <ol className="mt-2 list-decimal space-y-2 pl-5 text-sm leading-relaxed text-slate-400">
                                {citations.map((citation, i) => (
                                    <li key={i}>{citation}</li>
                                ))}
                            </ol>
                        </div>
                    )}
                    <button
                        type="button"
                        onClick={() => setExpanded(false)}
                        className="mt-4 text-xs text-slate-500 hover:text-slate-300 transition-colors"
                    >
                        Collapse ↑
                    </button>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => setExpanded(true)}
                    className="mt-4 w-full rounded-xl border border-white/10 bg-slate-950/50 py-3 text-sm font-medium text-slate-400 hover:text-slate-200 hover:border-white/20 transition-colors"
                >
                    Show Answer
                </button>
            )}

            {Array.isArray(meta.tags) && meta.tags.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                    {meta.tags.map((tag) => (
                        <span
                            key={tag}
                            className="inline-flex items-center rounded-full border border-slate-600/80 px-2.5 py-0.5 text-[11px] text-slate-400"
                        >
                            {tag}
                        </span>
                    ))}
                </div>
            )}
        </article>
    );
};

export default ClinicalGeneratedCard;
